/// <reference lib="dom" />
// Hermes Bridge - Connect ONE-TASK-VIEW to Hermes Agent Gateway
// Uses OmniRoute (localhost:20128) for delegation, skills, memory, kanban

export interface HermesConfig {
  baseUrl: string;
  apiKey?: string;
  defaultModel: string;
}

export interface DelegationTask {
  goal: string;
  context: string;
  outputSchema?: Record<string, unknown>;
  images?: string[];
}

export interface DelegationResult {
  delegationId: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  summary?: string;
  metadata?: Record<string, unknown>;
  result?: string;
  error?: string;
}

export interface SkillCall {
  name: string;
  input: Record<string, unknown>;
}

export interface SkillResult {
  success: boolean;
  output?: unknown;
  error?: string;
}

export interface MemoryEntry {
  key: string;
  content: string;
  target: 'user' | 'memory';
}

export interface KanbanTask {
  title: string;
  assignee: string;
  body?: string;
  parents?: string[];
  priority?: number;
  skills?: string[];
}

const DEFAULT_CONFIG: HermesConfig = {
  baseUrl: 'http://localhost:20128/v1',
  defaultModel: 'auto/best-free',
};

class HermesBridge {
  private config: HermesConfig;
  private abortControllers: Map<string, AbortController> = new Map();

  constructor(config: Partial<HermesConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- Core HTTP Helper ---
  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.config.baseUrl}${endpoint}`;
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    };

    if (this.config.apiKey) {
      headers['Authorization'] = `Bearer ${this.config.apiKey}`;
    }

    const response = await fetch(url, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => 'Unknown error');
      throw new Error(`Hermes API ${response.status}: ${errorText}`);
    }

    return response.json();
  }

  // --- DELEGATION ---
  async delegate(task: DelegationTask): Promise<DelegationResult> {
    // Use the delegation endpoint via OmniRoute
    // This calls the Hermes delegation tool internally
    const response = await this.request<DelegationResult>('/delegation/spawn', {
      method: 'POST',
      body: JSON.stringify({
        tasks: [{
          goal: task.goal,
          context: task.context,
          output_schema: task.outputSchema,
          images: task.images,
        }],
      }),
    });
    return response;
  }

  async pollDelegation(delegationId: string): Promise<DelegationResult> {
    return this.request<DelegationResult>(`/delegation/${delegationId}`);
  }

  async waitForDelegation(
    delegationId: string,
    options: { intervalMs?: number; timeoutMs?: number; onProgress?: (status: DelegationResult) => void } = {}
  ): Promise<DelegationResult> {
    const { intervalMs = 2000, timeoutMs = 300000, onProgress } = options;
    const startTime = Date.now();
    const controller = new AbortController();
    this.abortControllers.set(delegationId, controller);

    try {
      while (!controller.signal.aborted) {
        if (Date.now() - startTime > timeoutMs) {
          throw new Error(`Delegation timeout after ${timeoutMs}ms`);
        }

        const result = await this.pollDelegation(delegationId);
        onProgress?.(result);

        if (result.status === 'completed' || result.status === 'failed') {
          return result;
        }

        await new Promise(resolve => setTimeout(resolve, intervalMs));
      }
      throw new Error('Delegation aborted');
    } finally {
      this.abortControllers.delete(delegationId);
    }
  }

  abortDelegation(delegationId: string): void {
    const controller = this.abortControllers.get(delegationId);
    if (controller) {
      controller.abort();
      this.abortControllers.delete(delegationId);
    }
  }

  // --- SKILLS ---
  async callSkill(skillCall: SkillCall): Promise<SkillResult> {
    return this.request<SkillResult>('/skills/call', {
      method: 'POST',
      body: JSON.stringify(skillCall),
    });
  }

  async listSkills(): Promise<string[]> {
    return this.request<string[]>('/skills/list');
  }

  // --- MEMORY ---
  async memoryGet(key: string, target: 'user' | 'memory' = 'memory'): Promise<string | null> {
    try {
      return await this.request<string>(`/memory/${target}/${encodeURIComponent(key)}`);
    } catch {
      return null;
    }
  }

  async memorySet(entry: MemoryEntry): Promise<boolean> {
    try {
      await this.request(`/memory/${entry.target}`, {
        method: 'POST',
        body: JSON.stringify({ key: entry.key, content: entry.content }),
      });
      return true;
    } catch {
      return false;
    }
  }

  async memoryList(target: 'user' | 'memory' = 'memory'): Promise<string[]> {
    return this.request<string[]>(`/memory/${target}/list`);
  }

  async memoryDelete(key: string, target: 'user' | 'memory' = 'memory'): Promise<boolean> {
    try {
      await this.request(`/memory/${target}/${encodeURIComponent(key)}`, {
        method: 'DELETE',
      });
      return true;
    } catch {
      return false;
    }
  }

  // --- KANBAN ---
  async kanbanCreate(task: KanbanTask): Promise<{ id: string }> {
    return this.request<{ id: string }>('/kanban/create', {
      method: 'POST',
      body: JSON.stringify(task),
    });
  }

  async kanbanList(filters?: { assignee?: string; status?: string; limit?: number }): Promise<Array<{ id: string; title: string; status: string; assignee: string }>> {
    const params = new URLSearchParams();
    if (filters?.assignee) params.set('assignee', filters.assignee);
    if (filters?.status) params.set('status', filters.status);
    if (filters?.limit) params.set('limit', String(filters.limit));
    return this.request(`/kanban/list?${params.toString()}`);
  }

  async kanbanShow(taskId: string): Promise<unknown> {
    return this.request(`/kanban/${taskId}`);
  }

  async kanbanComplete(taskId: string, summary: string, metadata?: Record<string, unknown>): Promise<void> {
    await this.request(`/kanban/${taskId}/complete`, {
      method: 'POST',
      body: JSON.stringify({ summary, metadata }),
    });
  }

  // --- HEALTH / STATUS ---
    async healthCheck(): Promise<{ status: string; providers: string[] }> {
      // OmniRoute has /api/health at root level, not under /v1
      const rootUrl = this.config.baseUrl.replace(/\/v1\/?$/, '');
      const response = await fetch(`${rootUrl}/api/health`, {
        headers: {
          'Content-Type': 'application/json',
          ...(this.config.apiKey ? { 'Authorization': `Bearer ${this.config.apiKey}` } : {}),
        },
      });
      if (!response.ok) {
        throw new Error(`Hermes API ${response.status}: ${await response.text().catch(() => 'Unknown error')}`);
      }
      return response.json();
    }

  // --- CONFIG ---
  updateConfig(config: Partial<HermesConfig>): void {
    this.config = { ...this.config, ...config };
  }

  getConfig(): HermesConfig {
    return { ...this.config };
  }
}

// Singleton instance
export const hermesBridge = new HermesBridge();

// React Hook for easy usage in components
import { useState, useCallback, useEffect } from 'react';

export function useHermesBridge() {
  const [bridge] = useState(() => hermesBridge);
  const [isConnected, setIsConnected] = useState(false);
  const [lastError, setLastError] = useState<string | null>(null);

  const checkConnection = useCallback(async () => {
    try {
      await bridge.healthCheck();
      setIsConnected(true);
      setLastError(null);
    } catch (error) {
      setIsConnected(false);
      setLastError(error instanceof Error ? error.message : 'Connection failed');
    }
  }, [bridge]);

  useEffect(() => {
    checkConnection();
    const interval = setInterval(checkConnection, 30000);
    return () => clearInterval(interval);
  }, [checkConnection]);

  return {
    bridge,
    isConnected,
    lastError,
    checkConnection,
  };
}

// Convenience functions for common workflows
export async function delegateAndWait(
  goal: string,
  context: string,
  options: { onProgress?: (status: DelegationResult) => void; timeoutMs?: number } = {}
): Promise<DelegationResult> {
  const result = await hermesBridge.delegate({ goal, context });
  if (result.status === 'completed' || result.status === 'failed') {
    return result;
  }
  return hermesBridge.waitForDelegation(result.delegationId, options);
}

export async function quickResearch(topic: string): Promise<string> {
  const result = await delegateAndWait(
    `Research ${topic} and provide a concise summary with key findings and sources`,
    `User needs a quick research summary on: ${topic}. Focus on actionable insights, tools, and recent developments.`,
    { timeoutMs: 120000 }
  );
  return result.result || result.summary || 'No result returned';
}

export async function quickCodeTask(task: string, context: string): Promise<string> {
  const result = await delegateAndWait(
    `Implement: ${task}`,
    `Context: ${context}\n\nRequirements: Clean, typed, tested code. Follow project conventions.`,
    { timeoutMs: 180000 }
  );
  return result.result || result.summary || 'No code returned';
}